/**
 * TransferEditForm Component
 *
 * Edit mode form for modifying transfer lines and notes.
 * @see Sprint 14 - Transferts Inter-Sites
 * @see Sprint 22 - Transferts par Packaging
 */

import {
  Button,
  Input,
  Textarea,
  SearchAutocomplete,
  Select,
} from '@/components/ui';
import { getQuantityStep } from '@/lib/format';
import type { SearchAutocompleteOption } from '@/components/ui';
import { Trash2 } from 'lucide-react';
import type { EditLineState } from './useTransferDrawerState';

const BASE_UNIT_VALUE = '__base_unit__';

interface TransferEditFormProps {
  editLines: EditLineState[];
  editNotes: string;
  onEditNotesChange: (value: string) => void;
  productSearch: string;
  onProductSearchChange: (value: string) => void;
  productOptions: SearchAutocompleteOption[];
  searchingProducts: boolean;
  onAddLine: (option: SearchAutocompleteOption | null) => void;
  onRemoveLine: (id: string) => void;
  onQuantityChange: (id: string, quantity: number) => void;
  onPackagingChange: (lineId: string, value: string) => void;
  onPackagingQtyChange: (lineId: string, qtyStr: string) => void;
}

export function TransferEditForm({
  editLines,
  editNotes,
  onEditNotesChange,
  productSearch,
  onProductSearchChange,
  productOptions,
  searchingProducts,
  onAddLine,
  onRemoveLine,
  onQuantityChange,
  onPackagingChange,
  onPackagingQtyChange,
}: TransferEditFormProps) {
  return (
    <div className="space-y-4">
      {/* Add product search */}
      <div className="mb-3">
        <SearchAutocomplete
          placeholder="Rechercher un produit a ajouter..."
          value={null}
          onChange={onAddLine}
          options={productOptions}
          isLoading={searchingProducts}
          searchValue={productSearch}
          onSearchChange={onProductSearchChange}
          emptyMessage="Aucun produit trouve"
          minSearchLength={2}
        />
      </div>

      {/* Edit lines */}
      {editLines.length > 0 ? (
        <div className="space-y-2 max-h-60 overflow-auto">
          {editLines.map((line) => {
            const hasPackagingOptions = line.packagingOptions.length > 0;
            const selectedPkg = line.packagingId
              ? line.packagingOptions.find((o) => o.packagingId === line.packagingId)
              : null;

            return (
              <div
                key={line.id}
                className="p-3 bg-neutral-50 rounded-lg space-y-2"
              >
                {/* Product name + delete button */}
                <div className="flex items-center justify-between">
                  <div className="font-medium text-sm">{line.rawProductName}</div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onRemoveLine(line.id)}
                  >
                    <Trash2 className="w-4 h-4 text-danger-500" />
                  </Button>
                </div>
                {/* Unite + Quantite row */}
                <div className="flex items-end gap-3">
                  <div className="flex-1 min-w-0">
                    <label className="block text-xs text-neutral-500 mb-1">Unite</label>
                    {hasPackagingOptions ? (
                      <Select
                        value={line.packagingId ?? BASE_UNIT_VALUE}
                        onChange={(val) => onPackagingChange(line.id, val)}
                        options={[
                          { value: BASE_UNIT_VALUE, label: `Unite de base (${line.unit})` },
                          ...line.packagingOptions.map((o) => ({
                            value: o.packagingId,
                            label: o.label,
                          })),
                        ]}
                      />
                    ) : (
                      <div className="h-9 flex items-center text-sm text-neutral-600">
                        {line.unit}
                      </div>
                    )}
                  </div>
                  <div className="w-28">
                    <label className="block text-xs text-neutral-500 mb-1">Quantite</label>
                    {line.packagingId ? (
                      <Input
                        type="number"
                        min="0.01"
                        max={1000}
                        step={getQuantityStep()}
                        value={line.packagingQty}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value);
                          if (!isNaN(val) && val > 1000) return;
                          onPackagingQtyChange(line.id, e.target.value);
                        }}
                      />
                    ) : (
                      <Input
                        type="number"
                        min="0.01"
                        max={1000}
                        step={getQuantityStep()}
                        value={line.quantity}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value) || 0;
                          onQuantityChange(line.id, Math.min(val, 1000));
                        }}
                      />
                    )}
                  </div>
                </div>
                {/* Conversion display */}
                {line.packagingId && selectedPkg && (
                  <div className="text-xs text-neutral-500">
                    = {(parseFloat(line.packagingQty) || 0) * (line.conversionFactor ?? 0)} {line.unit}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8 text-neutral-400 border-2 border-dashed rounded-lg">
          Aucun produit - ajoutez-en via la recherche
        </div>
      )}

      {/* Edit notes */}
      <div className="mt-4">
        <label className="block text-sm font-medium text-neutral-700 mb-1">
          Notes
        </label>
        <Textarea
          value={editNotes}
          onChange={(e) => onEditNotesChange(e.target.value)}
          placeholder="Notes optionnelles..."
          rows={3}
        />
      </div>
    </div>
  );
}
